import React, { ReactNode, useEffect, useState } from "react";

import { useUrlPageState } from "../utilities/dataTablePagination";
import { userAPI } from "../UserAPI";
import Section from "../components/Section";
import { useTranslation } from "react-i18next";
import { useCustomerTheme } from "../Theme";
import Metric from "../components/Metric";

import type { Column, ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, RefreshCw, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface SecurityDashboard {
    timestamp: string;
    server: {
        uptime: string;
        startTime: Date;
    };
    rateLimits: {
        general: {
            windowMs: number;
            max: number;
            currentWindow: string;
            description: string;
        };
        auth: {
            windowMs: number;
            max: number;
            currentWindow: string;
            description: string;
        };
    };
    throughput: {
        timeWindow: number;
        totalRequests: number;
        suspiciousRequests: number;
        blockedRequests: number;
        requestsPerMinute: number;
        suspiciousPerMinute: number;
        blockedPerMinute: number;
        requestsPerHour: number;
        suspiciousPerHour: number;
        blockedPerHour: number;
    };
    blockedIPs: {
        total: number;
        currentlyBlocked: number;
        list: Array<{
            ip: string;
            count: number;
            blocked: boolean;
            lastSeen: Date;
            timeUntilUnblock: number;
            timeUntilUnblockFormatted: string;
        }>;
    };
    alerts: {
        highVolume: boolean;
        highSuspicious: boolean;
        highBlocked: boolean;
        lastAlertTime: Date;
        alertCooldown: number;
        timeUntilNextAlert: number;
        timeUntilNextAlertFormatted: string;
    };
    thresholds: {
        HIGH_VOLUME: number;
        HIGH_SUSPICIOUS: number;
        HIGH_BLOCKED: number;
    };
    security: {
        suspiciousPaths: number;
        legitimatePaths: number;
        suspiciousUserAgents: number;
        suspiciousThreshold: number;
        blockDuration: number;
        blockDurationFormatted: string;
    };
}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
    column,
    children,
}: {
    column: Column<any, unknown>;
    children: ReactNode;
}) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === "asc")}
        >
            {children}
            {sorted === "asc" ? (
                <ArrowUp aria-hidden className="h-3.5 w-3.5" />
            ) : sorted === "desc" ? (
                <ArrowDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
                <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
            )}
        </Button>
    );
}

/** Leerzustand einer Tabelle: Überschrift und Erklärung. */
function TableEmptyState({ title, hint }: { title: string; hint?: string }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
        </div>
    );
}

const SecurityPanel: React.FC = () => {
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const blockedIPsPagination = useUrlPageState('blockedIPsPage');
    const [dashboard, setDashboard] = useState<SecurityDashboard | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showUnblockDialog, setShowUnblockDialog] = useState(false);
    const [selectedIP, setSelectedIP] = useState<string>("");
    const [refreshCountdown, setRefreshCountdown] = useState(30);
    const [unblockLoading, setUnblockLoading] = useState(false);

    const loadDashboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await userAPI.getSecurityDashboard();
            setDashboard(data);
            setRefreshCountdown(30); // Reset countdown after successful refresh
        } catch (err) {
            setError(err instanceof Error ? err.message : t('monitoringPanel.security.error'));
        } finally {
            setLoading(false);
        }
    };

    const unblockIP = async (ip: string) => {
        setUnblockLoading(true);
        try {
            await userAPI.unblockIP(ip);
            await loadDashboard(); // Refresh dashboard
            setShowUnblockDialog(false);
            setSelectedIP("");
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fehler beim Entsperren der IP');
        } finally {
            setUnblockLoading(false);
        }
    };

    useEffect(() => {
        loadDashboard();

        // Auto-refresh every 30 seconds
        const refreshInterval = setInterval(() => {
            loadDashboard();
            setRefreshCountdown(30); // Reset countdown after refresh
        }, 30000);

        // Countdown timer that updates every second
        const countdownInterval = setInterval(() => {
            setRefreshCountdown(prev => {
                if (prev <= 1) {
                    return 30; // Reset to 30 when it reaches 0
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(refreshInterval);
            clearInterval(countdownInterval);
        };
    }, []);

    const blockedIPColumns: ColumnDef<any>[] = [
        {
            id: 'ip',
            size: 150,
            accessorFn: (row: any) => row.ip,
            header: ({ column }) => <SortableHeader column={column}>IP-Adresse</SortableHeader>,
            cell: ({ row }) => (
                <div className="min-w-0 break-all font-mono text-sm">{row.original.ip}</div>
            ),
        },
        {
            id: 'count',
            size: 100,
            accessorFn: (row: any) => row.count,
            header: ({ column }) => <SortableHeader column={column}>Anfragen</SortableHeader>,
            cell: ({ row }) => <div className="text-sm tabular-nums">{row.original.count}</div>,
        },
        {
            id: 'blocked',
            size: 120,
            accessorFn: (row: any) => row.blocked,
            header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
            cell: ({ row }) => (
                <div className="text-sm">
                    <span className={row.original.blocked ? "font-bold text-destructive" : "text-warning"}>
                        {row.original.blocked ? 'GESPERRT' : 'Verdächtig'}
                    </span>
                </div>
            ),
        },
        {
            id: 'lastSeen',
            size: 180,
            accessorFn: (row: any) => (row.lastSeen ? new Date(row.lastSeen).getTime() : 0),
            header: ({ column }) => <SortableHeader column={column}>Letzte Aktivität</SortableHeader>,
            cell: ({ row }) => (
                <div className="whitespace-nowrap text-sm">
                    {new Date(row.original.lastSeen).toLocaleString('de-DE')}
                </div>
            ),
        },
        {
            id: 'timeUntilUnblock',
            size: 120,
            accessorFn: (row: any) => row.timeUntilUnblockFormatted,
            header: ({ column }) => <SortableHeader column={column}>Entsperren in</SortableHeader>,
            cell: ({ row }) => (
                <div className="whitespace-nowrap text-sm">
                    {row.original.blocked ? row.original.timeUntilUnblockFormatted : '-'}
                </div>
            ),
        },
        {
            id: 'actions',
            size: 120,
            enableSorting: false,
            header: () => 'Aktionen',
            cell: ({ row }) => (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!row.original.blocked}
                    onClick={() => {
                        setSelectedIP(row.original.ip);
                        setShowUnblockDialog(true);
                    }}
                >
                    Entsperren
                </Button>
            ),
        },
    ];

    const hasActiveAlerts = dashboard?.alerts.highVolume ||
                           dashboard?.alerts.highSuspicious ||
                           dashboard?.alerts.highBlocked;

    if (loading && !dashboard) {
        return (
            <div
                className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6"
                role="status"
                aria-live="polite"
            >
                <span className="sr-only">{t('monitoringPanel.security.loading')}</span>
                <Skeleton className="h-[140px] w-full" />
                <Skeleton className="h-[160px] w-full" />
                <Skeleton className="h-[300px] w-full" />
            </div>
        );
    }

    return (
        <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
            {error && (
                <Alert variant="destructive">
                    <div className="flex min-w-0 items-center gap-2">
                        <AlertDescription className="min-w-0 flex-1 break-words">{error}</AlertDescription>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="-my-2 -mr-2 shrink-0"
                            aria-label={t('global.close')}
                            onClick={() => setError(null)}
                        >
                            <X aria-hidden className="h-4 w-4" />
                        </Button>
                    </div>
                </Alert>
            )}

            {hasActiveAlerts && (
                <Alert variant="warning">
                    <AlertTitle>{t('monitoringPanel.security.activeAlerts')}</AlertTitle>
                    <AlertDescription>
                        <ul className="list-disc pl-5">
                            {dashboard?.alerts.highVolume && (
                                <li>Hohes Volumen: {dashboard.throughput.requestsPerHour} Anfragen/Stunde</li>
                            )}
                            {dashboard?.alerts.highSuspicious && (
                                <li>Hohe verdächtige Aktivität: {dashboard.throughput.suspiciousPerHour}/Stunde</li>
                            )}
                            {dashboard?.alerts.highBlocked && (
                                <li>Viele blockierte Anfragen: {dashboard.throughput.blockedPerHour}/Stunde</li>
                            )}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2">
                <div className="text-sm text-muted-foreground" aria-live="off">
                    Nächste Aktualisierung in {refreshCountdown}s
                </div>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Aktualisieren"
                                onClick={loadDashboard}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw aria-hidden className="h-4 w-4" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('monitoringPanel.security.refresh')}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            {/* Server Status */}
            <Section title="Server-Status">
                <div className="flex min-w-0 flex-wrap gap-6">
                    <Metric
                        title="Betriebszeit"
                        value={dashboard?.server.uptime?.replace('minutes', 'min').replace('minute', 'min') || '-'}
                        color={theme.primary}
                        className="p-5"
                    />
                    <Metric
                        title="Startzeit"
                        value={dashboard?.server.startTime ? new Date(dashboard.server.startTime).toLocaleString('de-DE') : '-'}
                        color={theme.secondary}
                        className="min-w-0 grow basis-[500px] p-5"
                    />
                </div>
            </Section>

            {/* Throughput Statistics */}
            <Section title="Durchsatz-Statistiken">
                <div className="flex min-w-0 flex-wrap gap-6">
                    <Metric
                        title="Gesamt-Anfragen"
                        value={dashboard?.throughput.totalRequests.toLocaleString('de-DE') || '0'}
                        unit={`${dashboard?.throughput.requestsPerMinute || 0}/min`}
                        color={theme.primary}
                        className="p-5"
                    />
                    <Metric
                        title="Verdächtige Anfragen"
                        value={dashboard?.throughput.suspiciousRequests.toLocaleString('de-DE') || '0'}
                        unit={`${dashboard?.throughput.suspiciousPerMinute || 0}/min`}
                        color={theme.secondary}
                        className="p-5"
                    />
                    <Metric
                        title="Blockierte Anfragen"
                        value={dashboard?.throughput.blockedRequests.toLocaleString('de-DE') || '0'}
                        unit={`${dashboard?.throughput.blockedPerMinute || 0}/min`}
                        color={theme.tertiary}
                        className="p-5"
                    />
                </div>
            </Section>

            {/* Rate Limits and Alert Thresholds */}
            <div className="grid min-w-0 gap-6 md:grid-cols-2">
                <Section title="Rate Limits">
                    <div className="flex min-w-0 flex-wrap gap-6">
                        <div className="flex flex-col items-center">
                            <Metric
                                title={dashboard?.rateLimits.general.description || 'Generelle API-Anfragen'}
                                value={`${dashboard?.rateLimits.general.max.toLocaleString('de-DE') || 0}`}
                                color={theme.primary}
                                className="p-5"
                            />
                            <div className="mt-1 text-sm text-muted-foreground">
                                Fenster: {dashboard?.rateLimits.general.currentWindow?.replace('minutes', 'min').replace('minute', 'min') || '-'}
                            </div>
                        </div>
                        <div className="flex flex-col items-center">
                            <Metric
                                title={dashboard?.rateLimits.auth.description || 'Authentifizierungsanfragen'}
                                value={`${dashboard?.rateLimits.auth.max.toLocaleString('de-DE') || 0}`}
                                color={theme.secondary}
                                className="p-5"
                            />
                            <div className="mt-1 text-sm text-muted-foreground">
                                Fenster: {dashboard?.rateLimits.auth.currentWindow?.replace('minutes', 'min').replace('minute', 'min') || '-'}
                            </div>
                        </div>
                    </div>
                </Section>

                <Section title="Warnschwellen">
                    <div className="flex min-w-0 flex-wrap gap-6">
                        <Metric
                            title="Hohes Volumen"
                            value={dashboard?.thresholds.HIGH_VOLUME.toLocaleString('de-DE') || '0'}
                            unit="/h"
                            color={theme.primary}
                            className="p-5"
                        />
                        <Metric
                            title="Hohe Verdächtigkeit"
                            value={dashboard?.thresholds.HIGH_SUSPICIOUS.toLocaleString('de-DE') || '0'}
                            unit="/h"
                            color={theme.secondary}
                            className="p-5"
                        />
                        <Metric
                            title="Viele Blockierungen"
                            value={dashboard?.thresholds.HIGH_BLOCKED.toLocaleString('de-DE') || '0'}
                            unit="/h"
                            color={theme.tertiary}
                            className="p-5"
                        />
                    </div>
                </Section>
            </div>

            {/* Blocked IPs */}
            <Section
                title={
                    dashboard
                        ? `Blockierte IPs (${dashboard.blockedIPs.currentlyBlocked}/${dashboard.blockedIPs.total})`
                        : 'Blockierte IPs'
                }
            >
                <div className="min-w-0 overflow-x-auto">
                    <DataTable
                        columns={blockedIPColumns}
                        data={dashboard?.blockedIPs.list || []}
                        pageSize={10}
                        page={blockedIPsPagination.page}
                        onPageChange={blockedIPsPagination.onPageChange}
                        previousAriaLabel={t('global.back')}
                        nextAriaLabel={t('global.next')}
                        emptyMessage={
                            <TableEmptyState
                                title={t('monitoringPanel.security.noBlockedIPsTitle')}
                                hint={t('monitoringPanel.security.noBlockedIPsHint')}
                            />
                        }
                    />
                </div>
            </Section>

            {/* Security Configuration */}
            <Section title="Sicherheitskonfiguration">
                <div className="flex min-w-0 flex-wrap gap-6">
                    <Metric
                        title="Verdächtige Pfade"
                        value={dashboard?.security.suspiciousPaths?.toString() || '0'}
                        color={theme.primary}
                        className="p-5"
                    />
                    <Metric
                        title="Legitime Pfade"
                        value={dashboard?.security.legitimatePaths?.toString() || '0'}
                        color={theme.secondary}
                        className="p-5"
                    />
                    <Metric
                        title="Verdächtige User Agents"
                        value={dashboard?.security.suspiciousUserAgents?.toString() || '0'}
                        color={theme.tertiary}
                        className="p-5"
                    />
                    <Metric
                        title="Sperrschwelle"
                        value={`${dashboard?.security.suspiciousThreshold || 0}`}
                        color={theme.primary}
                        className="p-5"
                    />
                    <Metric
                        title="Sperrdauer"
                        value={dashboard?.security.blockDurationFormatted?.replace('minutes', 'min').replace('minute', 'min') || '-'}
                        color={theme.secondary}
                        className="p-5"
                    />
                </div>
            </Section>

            {/* Unblock IP Dialog */}
            <Dialog
                open={showUnblockDialog}
                onOpenChange={(open) => { if (!open) setShowUnblockDialog(false); }}
            >
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>IP-Adresse entsperren</DialogTitle>
                        <DialogDescription className="min-w-0 break-words">
                            Möchten Sie die IP-Adresse {selectedIP} entsperren?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            className="max-sm:w-full"
                            onClick={() => setShowUnblockDialog(false)}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            className="max-sm:w-full"
                            onClick={() => unblockIP(selectedIP)}
                            disabled={unblockLoading}
                        >
                            {unblockLoading && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            Entsperren
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SecurityPanel;
